import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../../shared/api/client";
import type { WarehouseUser, Responsibility, WarehouseAssignment } from "./settings-types";
export function useWarehouseResponsibilitiesWorkspace({ warehouseUsers, selectedUserId, notify }: {
    warehouseUsers: WarehouseUser[];
    selectedUserId: string;
    notify: (message: string) => void;
}) {
    const [assignments, setAssignments] = useState<WarehouseAssignment[]>([]);
    const [assignmentWarehouseCode, setAssignmentWarehouseCode] = useState("");
    const [responsibility, setResponsibility] = useState<Responsibility>("MAIN");
    const [assignmentsLoading, setAssignmentsLoading] = useState(false);
    const [assignmentWorking, setAssignmentWorking] = useState(false);
    const [assignmentError, setAssignmentError] = useState<string | null>(null);
    const selectedWarehouseUser = useMemo(() => warehouseUsers.find((user) => user.id === selectedUserId) ?? null, [selectedUserId, warehouseUsers]);
    const responsibilityOptions = useMemo(() => {
        const options: Array<{
            value: Responsibility;
            label: string;
        }> = [];
        if (selectedWarehouseUser?.roles.includes("DEPO_GOREVLISI"))
            options.push({ value: "MAIN", label: "Ana depo" }, { value: "HANDOFF", label: "Teslim / tesellüm" });
        if (selectedWarehouseUser?.roles.includes("CEP_DEPO_SORUMLUSU"))
            options.push({ value: "STAGING", label: "Cep depo" });
        return options;
    }, [selectedWarehouseUser]);
    useEffect(() => {
        if (!responsibilityOptions.some((option) => option.value === responsibility))
            setResponsibility(responsibilityOptions[0]?.value ?? "MAIN");
    }, [responsibility, responsibilityOptions]);
    useEffect(() => {
        if (!selectedUserId) {
            setAssignments([]);
            setAssignmentsLoading(false);
            return;
        }
        let current = true;
        setAssignmentsLoading(true);
        setAssignmentError(null);
        apiRequest<{
            items: WarehouseAssignment[];
        }>(`/api/material-preparation/user-warehouses/${selectedUserId}`, { cache: "no-store" })
            .then((result) => {
            if (current)
                setAssignments(result.items);
        })
            .catch((loadError) => {
            if (!current)
                return;
            setAssignments([]);
            setAssignmentError(loadError instanceof Error
                ? loadError.message
                : "Depo sorumlulukları alınamadı.");
        })
            .finally(() => {
            if (current)
                setAssignmentsLoading(false);
        });
        return () => {
            current = false;
        };
    }, [selectedUserId]);
    const saveAssignment = async (active: boolean, existing?: WarehouseAssignment) => {
        const warehouseCode = existing?.warehouseCode ?? assignmentWarehouseCode;
        const assignmentResponsibility = existing?.responsibility ?? responsibility;
        if (!selectedUserId || !warehouseCode) {
            setAssignmentError("Kullanıcı ve Mikro depo kodu seçilmelidir.");
            return;
        }
        setAssignmentWorking(true);
        setAssignmentError(null);
        try {
            const saved = await apiRequest<WarehouseAssignment>(`/api/material-preparation/user-warehouses/${selectedUserId}/${encodeURIComponent(warehouseCode)}`, {
                method: "PUT",
                body: JSON.stringify({
                    responsibility: assignmentResponsibility,
                    active,
                    expectedVersion: existing?.version ?? 0,
                }),
            });
            setAssignments((current) => [
                ...current.filter((item) => item.warehouseCode !== saved.warehouseCode ||
                    item.responsibility !== saved.responsibility),
                saved,
            ]);
            setAssignmentWarehouseCode("");
            notify(`${selectedWarehouseUser?.name ?? "Kullanıcı"} için ${saved.warehouseCode} depo sorumluluğu ${saved.active ? "etkinleştirildi" : "pasifleştirildi"}.`);
        }
        catch (saveError) {
            setAssignmentError(saveError instanceof Error
                ? saveError.message
                : "Depo sorumluluğu kaydedilemedi.");
        }
        finally {
            setAssignmentWorking(false);
        }
    };
    return { assignments, assignmentWarehouseCode, setAssignmentWarehouseCode, responsibility, setResponsibility, assignmentsLoading, assignmentWorking, assignmentError, responsibilityOptions, saveAssignment };
}
